import { FC } from 'react';
import Box from '@/components/UI/box/Box';
import Icon from '@/components/UI/icon/Icon';
import Typography from '@/components/UI/typography/Typography';
import Divider from '@/components/UI/divider/Divider';
import styles from './HouseInformation.module.scss';
import { useMidTermQuery } from '@/components/houses/_hooks/useMidterm';
import { ProductItemTypes } from '@/components/houses/_types/productItem.types';
type Props = {
  props: ProductItemTypes;
};
const textures = {
  1: 'شهری',
  2: 'روستایی',
  3: 'ساحلی',
  4: 'جنگلی',
  5: 'کویری',
  6: 'کوهستانی'
};
const types = {
  villa: 'ویلا',
  apartment: 'خانه و آپارتمان',
  boomgardi: 'بوم‌گردی',
  classic: 'اقامتگاه سنتی',
  cottage: 'کلبه',
  omg: 'اقامتگاه های خاص'
};
const stairsOptions = {
  zero: 'اقامتگاه پله ندارد.',
  'less-than-5': 'کمتر از ۵ پله',
  'between-5-and-20': 'بین ۵ تا ۲۰ پله',
  'between-20-and-50': 'بین ۲۰ تا ۵۰ پله',
  'more-than-50': 'بیشتر از ۵۰ پله'
};
const sleepAmenities = {
  double_beds: ' تخت دونفره',
  single_beds: ' تخت یک نفره',
  sofa_beds: ' مبل تخت خواب شو',
  floor_sleep_sets: ' رخت خواب سنتی'
};
const WCAmenities = {
  bathroom: ' حمام',
  european_wc: ' سرویس فرنگی',
  iranian_wc: ' سرویس ایرانی'
};
const generateHouseName = data => {
  let type = data.type ? types[data.type] : '';
  switch (data?.type) {
    case 'villa':
      type = 'ویلا و سوئیت';
      break;
    case 'apartment':
      type = 'خانه و آپارتمان';
      break;
    case 'boomgardi':
      type = 'اقامتگاه بومگردی';
      break;
    case 'cottage':
      type = 'کلبه';
      break;
    default:
      type = 'اقامتگاه';
      break;
  }
  const structure = data.structure ? data.structure : '';
  const texture = data.location?.texture_type ? textures[data.location.texture_type] : '';
  const buildingArea = data.building_area ? `${data.building_area} متری` : '';
  return `${type} ${structure} ${texture} ${buildingArea}`;
};
const generateHouseInfo = data => {
  let res = '';
  const masterRoom = data?.spaces?.master_rooms ? `${data.spaces.master_rooms} اتاق مستر ` : '';
  const normalRoom = data?.spaces?.normal_rooms ? ` ${data.spaces.normal_rooms} اتاق غیر مستر ` : '';
  const floorNo = !data.is_units_count ? data?.floor_no === 0 ? `  همکف` : ` طبقه ${data?.floor_no}` : '';
  const stepNumber = !data.is_units_count && data.steps_range ? `${stairsOptions[data.steps_range]}` : '';
  const items = [masterRoom, normalRoom, floorNo, stepNumber];
  for (const item of items) {
    if (item) {
      if (res) {
        res += ' ، ';
      }
      res += item;
    }
  }
  return res;
};
const generateCapacity = data => {
  const extraCapacity = data?.rules?.records?.max_accommodates - data?.rules?.records?.accommodates;
  const accommodate = data?.rules?.records?.accommodates ? ` ${data?.rules?.records?.accommodates} نفر استاندارد` : '';
  const extraText = extraCapacity ? `+ ${extraCapacity} نفر اضافه` : '';
  return `${accommodate} ${extraText}`;
};
const generateBeds = data => {
  let res = '';
  if (!data.spaces) return res;
  const items = ['double_beds', 'single_beds', 'sofa_beds', 'floor_sleep_sets'];
  for (const item of items) {
    if (data?.spaces?.[item]) {
      if (res) {
        res += '، ';
      }
      res += data?.spaces[item] + sleepAmenities[item];
    }
  }
  return res;
};
const generateBath = data => {
  let res = '';
  data?.features?.filled_categories?.spaces?.forEach(item => {
    if ((item.feature_name === 'european_wc' || item.feature_name === 'iranian_wc' || item.feature_name === 'bathroom') && item?.meta_data?.count) {
      if (res) {
        res += '، ';
      }
      res += item.meta_data.count + WCAmenities[item.feature_name];
    }
  });
  return res;
};
const HouseInformation: FC<Props> = ({
  props
}) => {
  const isUnitCount = props?.is_units_count;
  return <Box className={styles['house-info']} data-sentry-element="Box" data-sentry-component="HouseInformation" data-sentry-source-file="HouseInformation.tsx">
      <Box className={styles['content']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
        <Box className={styles['info']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
          <Icon name="building-3" type="highEmphasis" data-sentry-element="Icon" data-sentry-source-file="HouseInformation.tsx" />
          <Box className={styles['info-content']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
            <Typography variant="bodyM_Bold" color="neutral_800" data-sentry-element="Typography" data-sentry-source-file="HouseInformation.tsx">
              {generateHouseName(props)}
            </Typography>
            <Typography variant="bodyM" color="secondary_main" data-sentry-element="Typography" data-sentry-source-file="HouseInformation.tsx">
              {generateHouseInfo(props)}
            </Typography>
          </Box>
        </Box>

        <Box className={styles['capacity']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
          <Icon name="profile-2user" type="highEmphasis" data-sentry-element="Icon" data-sentry-source-file="HouseInformation.tsx" />
          <Box className={styles['capacity-content']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
            <Typography variant="bodyM_Bold" color="neutral_800" data-sentry-element="Typography" data-sentry-source-file="HouseInformation.tsx">
              ظرفیت
            </Typography>
            <Typography variant="bodyM" color="secondary_main" data-sentry-element="Typography" data-sentry-source-file="HouseInformation.tsx">
              {generateCapacity(props)}
            </Typography>
          </Box>
        </Box>

        <Box className={styles['beds']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
          <Icon name="bed" type="highEmphasis" data-sentry-element="Icon" data-sentry-source-file="HouseInformation.tsx" />
          <Box className={styles['beds-content']} data-sentry-element="Box" data-sentry-source-file="HouseInformation.tsx">
            <Typography variant="bodyM_Bold" color="neutral_800" data-sentry-element="Typography" data-sentry-source-file="HouseInformation.tsx">
              {props?.spaces?.bed_space > 0 ? `${props?.spaces?.bed_space} جای خواب` : 'بدون جای خواب'}
            </Typography>
            <Typography variant="bodyM" color="secondary_main" data-sentry-element="Typography" data-sentry-source-file="HouseInformation.tsx">
              {generateBeds(props)}
            </Typography>
          </Box>
        </Box>
        {generateBath(props).trim() && <Box className={styles['bath']}>
            <Icon name="bathtub" type="highEmphasis" />
            <Box className={styles['bath-content']}>
              <Typography variant="bodyM_Bold" color="neutral_800">
                سرویس‌های بهداشتی
              </Typography>
              <Typography variant="bodyM" color="secondary_main">
                {generateBath(props)}
              </Typography>
            </Box>
          </Box>}
      </Box>
      {!isUnitCount && <Divider orientation="horizontal" />}
    </Box>;
};
export default HouseInformation;